/**
 * Learn catch-up briefing presentation (task-18, M0.5 client side).
 *
 * The daemon relay bridge forwards a completed catch-up run as an E2E-sealed
 * generic `event` envelope (no new MessageType):
 *
 *   { "event": "catchup.summary", "data": { title, body, data? } }
 *
 * The phone renders it as a local **system chat bubble**. This module validates
 * the payload, builds a deterministic turn id from a stable FNV-1a content hash
 * (so relay reconnects/queue replays never duplicate the bubble) and appends it
 * to a conversation. Everything is pure and offline-testable.
 *
 * Privacy: `title`/`body` may contain course names/announcement titles. They are
 * only handed to the local chat store; this module never logs or persists
 * anywhere beyond what AppState already stores in localStorage.
 */
import { nowSeconds } from '../id';
import type { ChatConversation, ChatTurn } from '../types';

export const CATCHUP_SUMMARY_EVENT = 'catchup.summary';
/** Long announcement titles are capped before the hash/UI. */
export const MAX_CATCHUP_TITLE_CHARS = 200;
/** Body cap kept well below the chat turn limit; HTML/Markdown is not trusted. */
export const MAX_CATCHUP_BODY_CHARS = 4000;
/** Final rendered content cap (title prefix + body + truncation notice). */
export const MAX_CATCHUP_CONTENT_CHARS = MAX_CATCHUP_BODY_CHARS + MAX_CATCHUP_TITLE_CHARS + 64;

export interface CatchUpSummary {
  title: string;
  body: string;
  /** Optional structured counters (e.g. `{ learn: { announcements, deadlines } }`). */
  data?: unknown;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Truncate by Unicode code points without depending on locale/segmentation. */
export function truncateText(text: string, maxChars: number, suffix = '…'): string {
  if (maxChars <= 0) return '';
  const chars = Array.from(text);
  if (chars.length <= maxChars) return text;
  const suffixChars = Array.from(suffix);
  if (suffixChars.length >= maxChars) return suffixChars.slice(0, maxChars).join('');
  return `${chars.slice(0, maxChars - suffixChars.length).join('')}${suffix}`;
}

/**
 * Strictly validate a decrypted `event` payload. Anything malformed is ignored
 * (returns null) so a broken/foreign event can never reach the UI or hash logic.
 */
export function parseCatchUpSummaryPayload(payload: unknown): CatchUpSummary | null {
  if (!isRecord(payload)) return null;
  if (payload['event'] !== CATCHUP_SUMMARY_EVENT) return null;
  const data = payload['data'];
  if (!isRecord(data)) return null;
  const rawTitle = data['title'];
  const rawBody = data['body'];
  if (typeof rawTitle !== 'string' || typeof rawBody !== 'string') return null;
  const title = truncateText(rawTitle.trim(), MAX_CATCHUP_TITLE_CHARS);
  const body = truncateText(rawBody.trim(), MAX_CATCHUP_BODY_CHARS);
  if (title.length === 0 || body.length === 0) return null;
  const summary: CatchUpSummary = { title, body };
  if (isRecord(data['data'])) summary.data = data['data'];
  return summary;
}

/** Stable FNV-1a 32-bit hash (deterministic across runtimes; not a crypto hash). */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function hashSegment(value: string): string {
  return fnv1a(value).toString(16).padStart(8, '0');
}

/**
 * Content hash for dedupe: title + body only (structured `data` is metadata and
 * must not create a "different" bubble for identical displayed content).
 */
export function catchUpSummaryHash(summary: Pick<CatchUpSummary, 'title' | 'body'>): string {
  return `${hashSegment(summary.title)}${hashSegment(summary.body)}`;
}

/** Deterministic turn id used to suppress replays (also across conversations). */
export function catchUpTurnId(summary: Pick<CatchUpSummary, 'title' | 'body'>): string {
  return `turn_catchup_${catchUpSummaryHash(summary)}`;
}

/** Markdown content mirrored by the existing system-bubble renderer. */
export function buildCatchUpTurnContent(summary: CatchUpSummary): string {
  const content = `**📚 ${summary.title}**\n\n${summary.body}`;
  return truncateText(content, MAX_CATCHUP_CONTENT_CHARS, '\n\n…（简报内容过长，已截断）');
}

export function buildCatchUpTurn(summary: CatchUpSummary, now = nowSeconds()): ChatTurn {
  return {
    id: catchUpTurnId(summary),
    role: 'system',
    content: buildCatchUpTurnContent(summary),
    reasoning: '',
    created_at: now,
    status: 'done',
  };
}

export function hasCatchUpTurn(
  conversations: readonly ChatConversation[],
  turnId: string,
): boolean {
  return conversations.some((conversation) =>
    conversation.turns.some((turn) => turn.id === turnId),
  );
}

/**
 * Append the briefing to `conversationId` as a system turn, idempotently:
 * - the turn id is derived from the content hash, so a queue replay of the same
 *   envelope is a no-op (this also covers switching conversations between a
 *   replay: the id is checked across all local conversations);
 * - a missing/unknown conversation is a no-op (never crash, never create one);
 * - the conversation turn cap is enforced.
 *
 * Returns the same array reference when nothing was appended so AppState can
 * skip a re-render.
 */
export function appendCatchUpBriefingTurn(
  conversations: ChatConversation[],
  conversationId: string,
  summary: CatchUpSummary,
  options: { limit?: number; now?: number } = {},
): ChatConversation[] {
  if (!conversationId) return conversations;
  const turn = buildCatchUpTurn(summary, options.now ?? nowSeconds());
  if (hasCatchUpTurn(conversations, turn.id)) return conversations;
  const index = conversations.findIndex((conversation) => conversation.id === conversationId);
  if (index < 0) return conversations;
  const limit = options.limit ?? 60;
  const target = conversations[index];
  if (!target) return conversations;
  const next = [...conversations];
  next[index] = {
    ...target,
    updated_at: turn.created_at,
    turns: [...target.turns, turn].slice(-Math.max(1, limit)),
  };
  return next;
}
